import { spawnSync } from "node:child_process";
import {
	mkdirSync,
	readdirSync,
	readFileSync,
	statSync,
	writeFileSync,
} from "node:fs";
import { dirname, join, resolve, sep } from "node:path";
import { unzipSync, zipSync } from "fflate";

export type SourceArchive =
	| { zip: Uint8Array; fileCount: number; note?: string }
	| { skipped: string };

// Directories that never belong in a source upload, at any depth.
const EXCLUDED_DIRS = new Set([
	".git",
	"node_modules",
	"dist",
	".output",
	".next",
	".turbo",
	".wrangler",
	".cache",
	"coverage",
]);

// .env.example-style templates are meant to be committed; every other
// .env / .dev.vars variant holds secrets.
const SAFE_ENV_SUFFIXES = [".example", ".sample", ".template"];

function isExcludedFile(name: string): boolean {
	if (name === ".DS_Store") return true;
	const envLike =
		name === ".env" ||
		name.startsWith(".env.") ||
		name === ".dev.vars" ||
		name.startsWith(".dev.vars.");
	return envLike && !SAFE_ENV_SUFFIXES.some((suffix) => name.endsWith(suffix));
}

// `path` is forward-slash relative to the project dir (git's output format,
// which the walk fallback matches).
function isExcluded(path: string): boolean {
	const segments = path.split("/");
	return (
		segments.slice(0, -1).some((dir) => EXCLUDED_DIRS.has(dir)) ||
		isExcludedFile(segments[segments.length - 1])
	);
}

/**
 * The files a git commit would contain (tracked + untracked, minus
 * gitignored), so the archive mirrors what a push to GitHub would include.
 * Returns null when the project isn't a git work tree or git isn't installed.
 */
function listGitFiles(projectDir: string): string[] | null {
	const listed = spawnSync(
		"git",
		["ls-files", "--cached", "--others", "--exclude-standard", "-z"],
		{ cwd: projectDir, encoding: "utf-8", maxBuffer: 128 * 1024 * 1024 },
	);
	if (listed.error || listed.status !== 0) return null;

	// Tracked-but-deleted files are listed too, so drop anything that isn't a
	// regular file on disk (deletions, submodule directories).
	return [...new Set(listed.stdout.split("\0").filter(Boolean))].filter(
		(path) => {
			try {
				return statSync(join(projectDir, path)).isFile();
			} catch {
				return false;
			}
		},
	);
}

function walkProject(projectDir: string, prefix = ""): string[] {
	const files: string[] = [];
	for (const entry of readdirSync(join(projectDir, prefix), {
		withFileTypes: true,
	})) {
		const path = prefix ? `${prefix}/${entry.name}` : entry.name;
		if (entry.isDirectory()) {
			if (!EXCLUDED_DIRS.has(entry.name)) {
				files.push(...walkProject(projectDir, path));
			}
		} else if (entry.isFile()) {
			files.push(path);
		}
	}
	return files;
}

/**
 * Packs the project source into a max-compression zip. In a git work tree the
 * file set is what a push to GitHub would contain (tracked + untracked, minus
 * gitignored); otherwise every project file is included. Both paths then drop
 * common junk that should never be uploaded — .git, node_modules, build
 * output, env/secret files.
 */
export function packSourceArchive(projectDir: string): SourceArchive {
	const gitFiles = listGitFiles(projectDir);
	const paths = (gitFiles ?? walkProject(projectDir))
		.filter((path) => !isExcluded(path))
		.sort();
	if (paths.length === 0) {
		return { skipped: "found no source files to archive" };
	}

	const contents: Record<string, Uint8Array> = {};
	for (const path of paths) {
		contents[path] = readFileSync(join(projectDir, path));
	}

	return {
		zip: zipSync(contents, { level: 9, mem: 12 }),
		fileCount: paths.length,
		note: gitFiles
			? undefined
			: "not a git repository — packed all project files minus common ignores",
	};
}

/**
 * Extracts a downloaded source archive into targetDir, refusing entries that
 * would escape it. Returns the number of files written.
 */
export function extractSourceArchive(
	zip: Uint8Array,
	targetDir: string,
): number {
	const root = resolve(targetDir);
	let count = 0;
	for (const [name, data] of Object.entries(unzipSync(zip))) {
		if (name.endsWith("/")) continue;
		const dest = resolve(root, name);
		if (!dest.startsWith(root + sep)) {
			throw new Error(`Archive entry escapes the target directory: ${name}`);
		}
		mkdirSync(dirname(dest), { recursive: true });
		writeFileSync(dest, data);
		count++;
	}
	return count;
}
